'use client'

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { STALE_TIMES, CACHE_TIMES } from '@/providers/QueryProvider'

// =====================================
// Query Keys - Centralized key management
// =====================================
export const hrKeys = {
  all: ['hr'] as const,
  
  // Time entries
  timeEntries: (staffId: string, clinicId: string) => 
    [...hrKeys.all, 'time-entries', staffId, clinicId] as const,
  timeEntriesWithRange: (staffId: string, clinicId: string, startDate?: string, endDate?: string) => 
    [...hrKeys.timeEntries(staffId, clinicId), startDate, endDate] as const,
  
  // Schedules
  schedules: (clinicId: string) => 
    [...hrKeys.all, 'schedules', clinicId] as const,
  schedulesWithRange: (clinicId: string, startDate: string, endDate: string, viewType: string) => 
    [...hrKeys.schedules(clinicId), startDate, endDate, viewType] as const,
  
  // Absences
  absences: (staffId: string, clinicId: string) => 
    [...hrKeys.all, 'absences', staffId, clinicId] as const,
  
  // Payroll
  payroll: (staffId: string, clinicId: string, month: string) => 
    [...hrKeys.all, 'payroll', staffId, clinicId, month] as const,
  
  // Flex time
  flexTime: (staffId: string, clinicId: string) => 
    [...hrKeys.all, 'flex-time', staffId, clinicId] as const,
}

// =====================================
// Types
// =====================================
interface TimeEntry {
  id: string
  user_id: string
  clock_in_time: string
  clock_out_time?: string
  total_hours?: number
  regular_hours?: number
  overtime_hours?: number
  status: string
  notes?: string
  is_corrected?: boolean
  corrected_by_role?: string
  correction_reason?: string
  is_deleted?: boolean
  staff?: {
    id: string
    full_name: string
    email: string
    role: string
  }
}

interface TimeEntriesResponse {
  timeEntries: TimeEntry[]
  statistics: {
    totalHours: number
    regularHours: number
    overtimeHours: number
    daysWorked: number
    averageHoursPerDay: number
  }
}

interface Shift {
  id: string
  user_id: string
  shift_date: string
  start_time: string
  end_time: string
  expected_hours: number
  shift_type: string
  status: string
  notes?: string
  user?: {
    id: string
    full_name: string
    role: string
  }
  template?: {
    name: string
    color_code: string
    template_type: string
  }
}

interface Holiday {
  holiday_date: string
  name_sv: string
  holiday_type: string
  is_day_off: boolean
}

interface ScheduleResponse {
  success: boolean
  shifts: Shift[]
  holidays: Holiday[]
}

// =====================================
// Fetch Functions
// =====================================
async function fetchTimeEntries(
  staffId: string,
  clinicId: string,
  startDate?: string,
  endDate?: string
): Promise<TimeEntriesResponse> {
  const params = new URLSearchParams({ staffId, clinicId })
  if (startDate) params.append('startDate', startDate)
  if (endDate) params.append('endDate', endDate)

  const response = await fetch(`/api/hr/time-tracking?${params}`)
  if (!response.ok) {
    throw new Error('Failed to fetch time entries')
  }
  return response.json()
}

async function fetchSchedule(
  clinicId: string,
  startDate: string,
  endDate: string,
  viewType: 'personal' | 'team',
  userId?: string,
  staffId?: string
): Promise<ScheduleResponse> {
  const params = new URLSearchParams({
    clinicId,
    startDate,
    endDate,
    viewType
  })
  if (viewType === 'personal' && userId) {
    params.append('userId', userId)
    if (staffId) params.append('staffId', staffId)
  }

  const response = await fetch(`/api/hr/schedules?${params}`)
  if (!response.ok) {
    throw new Error('Failed to fetch schedule')
  }
  return response.json()
}

// =====================================
// Query Hooks
// =====================================

/**
 * Fetch time entries with React Query caching
 * Data stays fresh for 5 minutes, cached for 15 minutes
 */
export function useTimeEntries(
  staffId: string,
  clinicId: string,
  startDate?: string,
  endDate?: string,
  enabled = true
) {
  return useQuery({
    queryKey: hrKeys.timeEntriesWithRange(staffId, clinicId, startDate, endDate),
    queryFn: () => fetchTimeEntries(staffId, clinicId, startDate, endDate),
    enabled: enabled && !!staffId && !!clinicId,
    staleTime: STALE_TIMES.HR_DATA,
    gcTime: CACHE_TIMES.MEDIUM,
  })
}

/**
 * Fetch schedule data with React Query caching
 */
export function useSchedule(
  clinicId: string,
  startDate: string,
  endDate: string,
  viewType: 'personal' | 'team',
  userId?: string,
  staffId?: string,
  enabled = true
) {
  return useQuery({
    queryKey: hrKeys.schedulesWithRange(clinicId, startDate, endDate, viewType),
    queryFn: () => fetchSchedule(clinicId, startDate, endDate, viewType, userId, staffId),
    enabled: enabled && !!clinicId && !!startDate && !!endDate,
    staleTime: STALE_TIMES.HR_DATA,
    gcTime: CACHE_TIMES.MEDIUM,
  })
}

/**
 * Mutation for clocking in/out
 */
export function useClockInOut() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: {
      staffId: string
      clinicId: string
      clockInTime: string
      clockOutTime?: string
      notes?: string
    }) => {
      const response = await fetch('/api/hr/time-tracking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
      if (!response.ok) throw new Error('Failed to clock in/out')
      return response.json()
    },
    onSuccess: (_, variables) => {
      // Invalidate time entries cache for this user
      queryClient.invalidateQueries({
        queryKey: hrKeys.timeEntries(variables.staffId, variables.clinicId)
      })
    }
  })
}

/**
 * Mutation for updating time entries
 */
export function useUpdateTimeEntry() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: {
      id: string
      staffId: string
      clinicId: string
      clockInTime?: string
      clockOutTime?: string
      correctionReason?: string
      correctedByUserId?: string
      correctedByRole?: string
    }) => {
      const response = await fetch('/api/hr/time-tracking', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
      if (!response.ok) throw new Error('Failed to update time entry')
      return response.json()
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: hrKeys.timeEntries(variables.staffId, variables.clinicId)
      })
    }
  })
}

/**
 * Prefetch time entries for faster navigation
 */
export function usePrefetchTimeEntries() {
  const queryClient = useQueryClient()
  
  return (staffId: string, clinicId: string, startDate?: string, endDate?: string) => {
    queryClient.prefetchQuery({
      queryKey: hrKeys.timeEntriesWithRange(staffId, clinicId, startDate, endDate),
      queryFn: () => fetchTimeEntries(staffId, clinicId, startDate, endDate),
      staleTime: STALE_TIMES.HR_DATA,
    })
  }
}
